import { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import Image from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";
import Highlight from "@tiptap/extension-highlight";
import "./JournalEditor.css";

const IMAGE_EXT_RE = /\.(jpe?g|png|gif|webp|heic|heif|avif|bmp|tif)$/i;

function isLikelyImageFile(file) {
  if (!file) {
    return false;
  }
  if (file.type.startsWith("image/")) {
    return true;
  }
  return IMAGE_EXT_RE.test(file.name || "");
}

/**
 * Resize and re-encode as JPEG so journal HTML stays reasonably small.
 * Prefer createImageBitmap (better WebP/HEIC support on Safari) then fall back to Image().
 */
async function compressImageToDataUrl(file, maxEdge, quality) {
  const drawToCanvas = (imageSource, bitmapToClose) => {
    try {
      const width = imageSource.width;
      const height = imageSource.height;
      const longest = Math.max(width, height);
      const scale = longest > maxEdge ? maxEdge / longest : 1;
      const w = Math.max(1, Math.round(width * scale));
      const h = Math.max(1, Math.round(height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        throw new Error("Canvas not supported");
      }
      ctx.drawImage(imageSource, 0, 0, w, h);
      return canvas.toDataURL("image/jpeg", quality);
    } finally {
      if (bitmapToClose && typeof bitmapToClose.close === "function") {
        try {
          bitmapToClose.close();
        } catch {
          /* ignore */
        }
      }
    }
  };

  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(file);
      return drawToCanvas(bitmap, bitmap);
    } catch {
      /* fall through to Image() path */
    }
  }

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        URL.revokeObjectURL(url);
        resolve(drawToCanvas(img, null));
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(
        new Error(
          "This browser could not decode that file. Try JPG or PNG, or on iPhone use “Most Compatible” in Camera settings."
        )
      );
    };
    img.src = url;
  });
}

export function JournalEditor({ initialHtml, placeholder, editorRef }) {
  const editorRefLatest = useRef(editorRef);
  editorRefLatest.current = editorRef;
  const fileInputRef = useRef(null);
  const [imageBusy, setImageBusy] = useState(false);

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: "noopener noreferrer nofollow" },
      }),
      Placeholder.configure({
        placeholder: placeholder || "Write your journal…",
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
        alignments: ["left", "center", "right"],
      }),
      Highlight.configure({ multicolor: false }),
      Image.configure({
        allowBase64: true,
        inline: false,
        HTMLAttributes: {
          class: "journal-inline-image",
        },
      }),
    ],
    content: initialHtml || "<p></p>",
    editorProps: {
      attributes: {
        class: "journal-prose",
      },
    },
  });

  useEffect(() => {
    const callback = editorRefLatest.current;
    if (callback) {
      callback(editor);
    }
    return () => {
      if (callback) {
        callback(null);
      }
    };
  }, [editor]);

  useEffect(() => {
    if (!editor || initialHtml === undefined) {
      return;
    }
    const current = editor.getHTML();
    if (initialHtml !== current) {
      editor.commands.setContent(initialHtml || "<p></p>", false);
    }
  }, [editor, initialHtml]);

  const onPickImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !editor) {
      return;
    }
    if (!isLikelyImageFile(file)) {
      window.alert("Please choose an image file (JPG, PNG, GIF, WebP, etc.).");
      return;
    }
    setImageBusy(true);
    try {
      const src = await compressImageToDataUrl(file, 1400, 0.82);
      const approxBytes = Math.ceil((src.length * 3) / 4);
      if (approxBytes > 1_800_000) {
        window.alert(
          "That image is still too large after compression. Try another photo or crop it first."
        );
        return;
      }
      editor.chain().focus().setImage({ src, alt: file.name.replace(/["<>]/g, "") }).run();
    } catch (err) {
      const msg =
        err && typeof err.message === "string" && err.message.trim()
          ? err.message.trim()
          : "Could not process this image.";
      window.alert(`${msg} Try JPG or PNG if this keeps happening.`);
    } finally {
      setImageBusy(false);
    }
  };

  if (!editor) {
    return <div className="journal-editor-shell">Loading editor…</div>;
  }

  const setLink = () => {
    const previous = editor.getAttributes("link").href;
    const url = window.prompt("Link URL", previous || "https://");
    if (url === null) {
      return;
    }
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className="journal-editor-shell">
      <div className="journal-toolbar" role="toolbar" aria-label="Rich text formatting">
        <span className="journal-toolbar-group" role="group" aria-label="Text style">
          <button
            type="button"
            className={`journal-tb-btn ${editor.isActive("bold") ? "is-active" : ""}`}
            onClick={() => editor.chain().focus().toggleBold().run()}
            aria-pressed={editor.isActive("bold")}
            aria-label="Bold"
            title="Bold"
          >
            <strong>B</strong>
          </button>
          <button
            type="button"
            className={`journal-tb-btn ${editor.isActive("italic") ? "is-active" : ""}`}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            aria-pressed={editor.isActive("italic")}
            aria-label="Italic"
            title="Italic"
          >
            <em>I</em>
          </button>
          <button
            type="button"
            className={`journal-tb-btn ${editor.isActive("underline") ? "is-active" : ""}`}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            aria-pressed={editor.isActive("underline")}
            aria-label="Underline"
            title="Underline"
          >
            <span className="journal-tb-underline">U</span>
          </button>
          <button
            type="button"
            className={`journal-tb-btn ${editor.isActive("strike") ? "is-active" : ""}`}
            onClick={() => editor.chain().focus().toggleStrike().run()}
            aria-pressed={editor.isActive("strike")}
            aria-label="Strikethrough"
            title="Strikethrough"
          >
            S
          </button>
          <button
            type="button"
            className={`journal-tb-btn ${editor.isActive("highlight") ? "is-active" : ""}`}
            onClick={() => editor.chain().focus().toggleHighlight().run()}
            aria-pressed={editor.isActive("highlight")}
            aria-label="Highlight"
            title="Highlight"
          >
            Hi
          </button>
        </span>
        <span className="toolbar-sep" aria-hidden />
        <span className="journal-toolbar-group" role="group" aria-label="Headings">
          <button
            type="button"
            className={`journal-tb-btn ${editor.isActive("heading", { level: 1 }) ? "is-active" : ""}`}
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            aria-label="Heading 1"
            title="Heading 1"
          >
            H1
          </button>
          <button
            type="button"
            className={`journal-tb-btn ${editor.isActive("heading", { level: 2 }) ? "is-active" : ""}`}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            aria-label="Heading 2"
            title="Heading 2"
          >
            H2
          </button>
          <button
            type="button"
            className={`journal-tb-btn ${editor.isActive("heading", { level: 3 }) ? "is-active" : ""}`}
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            aria-label="Heading 3"
            title="Heading 3"
          >
            H3
          </button>
          <button
            type="button"
            className={`journal-tb-btn ${
              editor.isActive("paragraph") && !editor.isActive("heading") ? "is-active" : ""
            }`}
            onClick={() => editor.chain().focus().setParagraph().run()}
            aria-label="Body text"
            title="Body"
          >
            Body
          </button>
        </span>
        <span className="toolbar-sep" aria-hidden />
        <span className="journal-toolbar-group" role="group" aria-label="Alignment">
          <button
            type="button"
            className={`journal-tb-btn ${editor.isActive({ textAlign: "left" }) ? "is-active" : ""}`}
            onClick={() => editor.chain().focus().setTextAlign("left").run()}
            aria-label="Align left"
            title="Align left"
          >
            ⟸
          </button>
          <button
            type="button"
            className={`journal-tb-btn ${editor.isActive({ textAlign: "center" }) ? "is-active" : ""}`}
            onClick={() => editor.chain().focus().setTextAlign("center").run()}
            aria-label="Align center"
            title="Align center"
          >
            ≡
          </button>
          <button
            type="button"
            className={`journal-tb-btn ${editor.isActive({ textAlign: "right" }) ? "is-active" : ""}`}
            onClick={() => editor.chain().focus().setTextAlign("right").run()}
            aria-label="Align right"
            title="Align right"
          >
            ⟹
          </button>
        </span>
        <span className="toolbar-sep" aria-hidden />
        <span className="journal-toolbar-group" role="group" aria-label="Lists and quote">
          <button
            type="button"
            className={`journal-tb-btn ${editor.isActive("bulletList") ? "is-active" : ""}`}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            aria-label="Bullet list"
            title="Bullet list"
          >
            • List
          </button>
          <button
            type="button"
            className={`journal-tb-btn ${editor.isActive("orderedList") ? "is-active" : ""}`}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            aria-label="Numbered list"
            title="Numbered list"
          >
            1. List
          </button>
          <button
            type="button"
            className={`journal-tb-btn ${editor.isActive("blockquote") ? "is-active" : ""}`}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            aria-label="Quote"
            title="Quote"
          >
            “ ”
          </button>
        </span>
        <span className="toolbar-sep" aria-hidden />
        <span className="journal-toolbar-group" role="group" aria-label="Insert">
          <button type="button" className="journal-tb-btn" onClick={setLink} title="Link">
            Link
          </button>
          <button
            type="button"
            className="journal-tb-btn journal-tb-btn-primary"
            disabled={imageBusy}
            onClick={() => fileInputRef.current?.click()}
            title="Insert image from your device"
          >
            {imageBusy ? "…" : "Image"}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="journal-image-input"
            aria-hidden
            tabIndex={-1}
            onChange={onPickImage}
          />
          <button
            type="button"
            className="journal-tb-btn"
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
            aria-label="Horizontal rule"
            title="Divider"
          >
            ─
          </button>
        </span>
      </div>
      <div className="journal-editor-content">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
